import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Star, AlertTriangle } from 'lucide-react';
import MatrixRain from '@/components/MatrixRain';
import MatrixToggle from '@/components/MatrixToggle';
import BarSpecialsPanel from '@/components/BarSpecialsPanel';
import VersionBadge from '@/components/VersionBadge';

const GREEN = '#00FF41';
const glow = { color: GREEN, textShadow: `0 0 8px rgba(0,255,65,0.85)` };
const mono = { fontFamily: 'JetBrains Mono, ui-monospace, monospace' };

// Sample data — real values will replace these when analytics endpoints wire up
const METRICS = [
  { label: "TONIGHT'S REVENUE", value: '$14,283', sub: '+23% vs last Sat' },
  { label: 'PATRONS NOW', value: '247', sub: '+18 in last hour' },
  { label: 'AVG SPEND', value: '$57.82', sub: '+$4.28 vs avg' },
  { label: 'CHECK-INS', value: '412', sub: '+31% tonight' },
  { label: 'RATING', value: '4.8', sub: 'Top 5% in city', star: true },
  { label: 'REPEAT VISITORS', value: '64%', sub: '+8% this month' },
];

const WEEKLY = [
  { d: 'Mon', v: 8.2, disp: '$8.2k' },
  { d: 'Tue', v: 6.4, disp: '$6.4k' },
  { d: 'Wed', v: 9.1, disp: '$9.1k' },
  { d: 'Thu', v: 12.3, disp: '$12.3k' },
  { d: 'Fri', v: 16.7, disp: '$16.7k' },
  { d: 'Sat', v: 18.1, disp: '$18.1k' },
  { d: 'Sun', v: 10.5, disp: '$10.5k' },
];

const HOURLY = [
  { h: '6PM', v: 18 }, { h: '7PM', v: 34 }, { h: '8PM', v: 55 },
  { h: '9PM', v: 72 }, { h: '10PM', v: 88 }, { h: '11PM', v: 95 },
  { h: '12AM', v: 100 }, { h: '1AM', v: 82 }, { h: '2AM', v: 45 },
  { h: '3AM', v: 22 }, { h: '4AM', v: 8 },
];

const DEMO = [
  { g: '21-25', pct: 32, color: '#FF3B4A' },
  { g: '26-30', pct: 28, color: '#00CCFF' },
  { g: '31-35', pct: 22, color: '#FFD700' },
  { g: '36-40', pct: 12, color: '#00FF41' },
  { g: '40+', pct: 6, color: '#A855F7' },
];

const TOP_CUSTOMERS = [
  { name: 'Alex Rivera', visits: 24, spend: '$1,842', status: 'HERE' },
  { name: 'Jordan Lee', visits: 19, spend: '$1,203', status: 'HERE' },
  { name: 'Samantha K', visits: 17, spend: '$987', status: 'EN ROUTE' },
  { name: 'Marcus T', visits: 15, spend: '$864', status: 'OFFLINE' },
  { name: 'Ivy Zhang', visits: 12, spend: '$742', status: 'HERE' },
];

const STAFF_OPS = [
  { role: 'Bartenders', onDuty: '4/4', status: 'FULL', ok: true },
  { role: 'Security', onDuty: '3/3', status: 'FULL', ok: true },
  { role: 'Servers', onDuty: '4/6', status: '-2', ok: false },
  { role: 'DJ / Host', onDuty: '1/1', status: 'FULL', ok: true },
  { role: 'Kitchen', onDuty: '2/3', status: '-1', ok: true },
];

function donutBg() {
  let acc = 0;
  const stops = DEMO.map((s) => { const from = acc; acc += s.pct; return `${s.color} ${from}% ${acc}%`; });
  return `conic-gradient(${stops.join(', ')})`;
}

export default function OwnerCommandCenter({ barName = 'BLUE LOUNGE', tagline = 'Downtown • Premium Nightlife Venue' }) {
  return (
    <div className="relative min-h-screen bg-black text-white" style={mono}>
      <MatrixRain />
      <MatrixToggle />
      <VersionBadge variant="owner-command-v1" />
      <div className="relative z-10 max-w-6xl mx-auto p-4 sm:p-6 space-y-5">

        {/* OS header */}
        <div className="flex flex-col sm:flex-row items-center justify-between border-b border-emerald-500/30 pb-2 gap-1">
          <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3">
            <span className="text-xs tracking-widest font-black" style={{ ...glow, fontFamily: 'Orbitron, sans-serif' }}>NIGHTBUZZ</span>
            <span className="text-[10px] tracking-widest" style={glow}>BAR OWNER · COMMAND CENTER · OS v1.0.0</span>
          </div>
          <span className="text-[10px] tracking-widest flex items-center gap-1" style={glow}>
            STATUS: <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> LIVE
          </span>
        </div>

        {/* Hero: bar name */}
        <div className="text-center py-3">
          <h1 data-testid="owner-hero-barname" className="font-black tracking-tight" style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 'clamp(2.5rem, 9vw, 5rem)', color: GREEN, textShadow: `0 0 20px rgba(0,255,65,0.85)` }}>
            {barName.toUpperCase()}
          </h1>
          <p className="text-xs sm:text-sm tracking-wide" style={glow}>{tagline}</p>
          <div className="flex justify-center flex-wrap gap-3 text-xs mt-2" style={glow}>
            <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-emerald-400" /> OPEN NOW</span>
            <span>Capacity: 73%</span>
            <span>Wait: ~12 min</span>
          </div>
        </div>

        {/* Metric grid 2x3 */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {METRICS.map((m) => (
            <Card key={m.label} data-testid={`metric-${m.label.toLowerCase().replace(/[^a-z]/g, '-')}`} className="bg-black/60 border border-emerald-500/30 p-3">
              <p className="text-[10px] font-mono tracking-widest" style={glow}>{m.label}</p>
              <p className="text-2xl sm:text-3xl font-black text-white flex items-center gap-1">
                {m.value} {m.star && <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />}
              </p>
              <p className="text-[10px] text-emerald-300/80">{m.sub}</p>
            </Card>
          ))}
        </div>

        {/* Weekly revenue */}
        <Card className="bg-black/60 border border-emerald-500/30 p-4">
          <p className="text-xs font-mono tracking-widest mb-3" style={glow}>◆ WEEKLY REVENUE</p>
          <div className="flex items-end justify-between h-32 gap-1">
            {WEEKLY.map((w) => (
              <div key={w.d} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-[9px] text-emerald-200">{w.disp}</span>
                <div className="w-full rounded-t" style={{ height: `${(w.v / 20) * 100}%`, background: `linear-gradient(180deg, #00FF41, #007A20)`, boxShadow: '0 0 8px rgba(0,255,65,0.5)' }} />
                <span className="text-[9px] font-mono" style={glow}>{w.d}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Hourly crowd flow */}
        <Card className="bg-black/60 border border-emerald-500/30 p-4">
          <p className="text-xs font-mono tracking-widest mb-3" style={glow}>◆ TONIGHT&apos;S CROWD FLOW (HOURLY)</p>
          <div className="flex items-end justify-between h-32 gap-1">
            {HOURLY.map((h) => (
              <div key={h.h} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full rounded-t" style={{ height: `${h.v}%`, background: `linear-gradient(180deg, #00FF41, #007A20)`, boxShadow: '0 0 8px rgba(0,255,65,0.5)' }} />
                <span className="text-[8px] font-mono" style={glow}>{h.h}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Demographics + Top Customers side-by-side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="bg-black/60 border border-emerald-500/30 p-4">
            <p className="text-xs font-mono tracking-widest mb-3" style={glow}>◆ TONIGHT&apos;S DEMOGRAPHICS</p>
            <div className="flex items-center gap-4">
              <div className="rounded-full flex-shrink-0" style={{ width: 120, height: 120, background: donutBg() }}>
                <div className="w-full h-full rounded-full flex flex-col items-center justify-center" style={{ background: 'radial-gradient(circle, #000 55%, transparent 56%)' }}>
                  <span className="text-[10px] font-mono" style={glow}>♂ 54%</span>
                  <span className="text-[10px] font-mono" style={{ color: '#FF3B4A' }}>♀ 46%</span>
                </div>
              </div>
              <div className="flex-1 space-y-1">
                {DEMO.map((d) => (
                  <div key={d.g} className="flex items-center gap-2 text-[11px] font-mono">
                    <span className="w-3 h-3 rounded-sm" style={{ background: d.color }} />
                    <span className="text-zinc-200">{d.g}</span>
                    <span className="ml-auto text-zinc-400">{d.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          <Card className="bg-black/60 border border-emerald-500/30 p-4">
            <p className="text-xs font-mono tracking-widest mb-3" style={glow}>◆ TOP CUSTOMERS TONIGHT</p>
            <div className="grid grid-cols-4 text-[9px] font-mono tracking-widest pb-1 border-b border-emerald-500/20" style={glow}>
              <span>PATRON</span><span>VISITS</span><span>SPEND</span><span>STATUS</span>
            </div>
            {TOP_CUSTOMERS.map((c) => (
              <div key={c.name} className="grid grid-cols-4 text-[11px] py-1.5 border-b border-emerald-500/10">
                <span className="text-white truncate">{c.name}</span>
                <span className="text-emerald-200">{c.visits}</span>
                <span className="text-emerald-300">{c.spend}</span>
                <Badge className={c.status === 'HERE' ? 'bg-emerald-600 justify-self-start' : c.status === 'EN ROUTE' ? 'bg-yellow-600 justify-self-start' : 'bg-zinc-600 justify-self-start'}>{c.status}</Badge>
              </div>
            ))}
          </Card>
        </div>

        {/* Active promotions */}
        <Card className="bg-black/60 border border-emerald-500/30 p-4 space-y-2">
          <p className="text-xs font-mono tracking-widest" style={glow}>◆ ACTIVE PROMOTIONS</p>
          <div className="flex items-center justify-between bg-emerald-900/20 border border-emerald-500/40 rounded p-2">
            <span className="text-sm text-white">Happy Hour 2-for-1</span>
            <Badge className="bg-emerald-500 text-black">LIVE</Badge>
          </div>
          <div className="flex items-center justify-between bg-emerald-900/20 border border-emerald-500/40 rounded p-2">
            <span className="text-sm text-white">Ladies Night Free Entry</span>
            <Badge className="bg-emerald-500 text-black">LIVE</Badge>
          </div>
          <div className="flex items-center justify-between bg-sky-900/20 border border-sky-500/40 rounded p-2">
            <span className="text-sm text-white">VIP Bottle Service -20%</span>
            <Badge className="bg-sky-500 text-black">SCHEDULED</Badge>
          </div>
        </Card>

        {/* Bar Specials Push Panel (real) */}
        <BarSpecialsPanel />

        {/* Staff & Operations */}
        <Card className="bg-black/60 border border-emerald-500/30 p-4">
          <p className="text-xs font-mono tracking-widest mb-3" style={glow}>◆ STAFF &amp; OPERATIONS</p>
          <div className="grid grid-cols-3 text-[9px] font-mono tracking-widest pb-1 border-b border-emerald-500/20" style={glow}>
            <span>ROLE</span><span>ON DUTY</span><span>STATUS</span>
          </div>
          {STAFF_OPS.map((s) => (
            <div key={s.role} className="grid grid-cols-3 text-xs py-1.5 border-b border-emerald-500/10">
              <span className="text-white">{s.role}</span>
              <span className="text-emerald-200">{s.onDuty}</span>
              <Badge className={s.ok ? 'bg-emerald-600 justify-self-start' : 'bg-red-600 justify-self-start'}>{s.status}</Badge>
            </div>
          ))}
          <p className="text-xs text-yellow-300 mt-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Server coverage light after 1AM — consider calling backup.</p>
        </Card>

        {/* Footer */}
        <div className="text-center pt-4 border-t border-emerald-500/20">
          <p className="text-sm tracking-widest font-black" style={{ ...glow, fontFamily: 'Orbitron, sans-serif' }}>GROW · MANAGE · PROFIT</p>
          <p className="text-[10px] font-mono mt-1 text-emerald-300/70">POWERED BY NIGHTBUZZ OS v1.0.0</p>
          <p className="text-[10px] font-mono mt-1 text-emerald-200/50">CONNECTED: Google Maps · Yelp · Instagram · Stripe · Uber/Lyft</p>
        </div>
      </div>
    </div>
  );
}
